import { Navigate, useLocation } from 'react-router-dom';

import { FeatureLock } from '@/components/FeatureLock';
import type { Permission } from '@/permissions/permissions.catalog';
import type { AccessVerdict } from '@/permissions/plan-access';
import { useAccessGate } from '@/permissions/useAccessGate';
import { usePermissions } from '@/permissions/usePermissions';
import { useAuth } from '@/store/auth/auth.store.ts';
import { setRedirectAfterAuth } from '@/utils/auth-redirect';
import type { UserRole } from '@/utils/roles';
import { hasAnyRole, hasAllRoles, hasRole } from '@/utils/roles';

type ProtectedRouteProps = {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: UserRole;
  requireAnyRole?: readonly UserRole[];
  requireAllRoles?: readonly UserRole[];
  denyRole?: UserRole;
  requirePermission?: Permission;
  requireAnyPermission?: readonly Permission[];
  requireAllPermissions?: readonly Permission[];
  skipPermissionsIfAnyRole?: readonly UserRole[];
  fallbackPath?: string;
  /**
   * The route's own name. Given it, an owner whose plan withholds this route sees a
   * placeholder saying so instead of being bounced to the dashboard with no explanation.
   * Without it the bounce stands, which is what every non-owner keeps getting either way.
   */
  lockTitle?: string;
};

export const ProtectedRoute = ({
  children,
  requireAuth = true,
  requireRole,
  requireAnyRole,
  requireAllRoles,
  denyRole,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions,
  skipPermissionsIfAnyRole,
  fallbackPath = '/auth',
  lockTitle,
}: ProtectedRouteProps) => {
  const { isLoggedIn, isInitializing, session } = useAuth();
  const { can, canAny, canAll } = usePermissions();
  const { verdictFor, anyVerdictFor, settled, planName, planGranted, upgradeHref } =
    useAccessGate();
  const location = useLocation();

  if (isInitializing) {
    return null;
  }

  if (requireAuth && !isLoggedIn) {
    setRedirectAfterAuth(location.pathname + location.search);
    return <Navigate to={fallbackPath} replace />;
  }

  if (requireRole && !hasRole(session?.userRoles, requireRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAnyRole && !hasAnyRole(session?.userRoles, requireAnyRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAllRoles && !hasAllRoles(session?.userRoles, requireAllRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (denyRole && hasRole(session?.userRoles, denyRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  const skipPermissions =
    !!skipPermissionsIfAnyRole && hasAnyRole(session?.userRoles, skipPermissionsIfAnyRole);

  if (!skipPermissions) {
    let denial: AccessVerdict | null = null;

    if (requirePermission && !can(requirePermission)) {
      denial = verdictFor(requirePermission);
    } else if (requireAnyPermission && !canAny([...requireAnyPermission])) {
      denial = anyVerdictFor(requireAnyPermission);
    } else if (requireAllPermissions && !canAll([...requireAllPermissions])) {
      denial = verdictFor([...requireAllPermissions]);
    }

    if (denial) {
      /**
       * A verdict reached before the subscription and the seat allow-lists have landed is
       * `hidden` whatever the plan actually says — nothing can be attributed to a plan that
       * is not known yet. Redirecting on that is irreversible: the route unmounts, and the
       * owner arrives at the dashboard having never seen the placeholder that was a
       * response or two away. So wait for the answer instead. The blank frame is the one
       * `isInitializing` above already renders, and only an owner ever waits for it —
       * `settled` is true from the first render for everybody else.
       */
      if (!settled) {
        return null;
      }

      if (denial === 'locked' && lockTitle) {
        return (
          <FeatureLock
            variant="page"
            title={lockTitle}
            planName={planName}
            upgradeHref={upgradeHref}
            planGranted={planGranted}
          />
        );
      }

      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};
